import {List} from 'immutable';
import {useCallback, useState} from 'react';
import {Edge} from 'reactflow';

import {
  isDecisionTreeBranchNodeDataFrontChat,
  NodeDataFrontChatTypesEnum,
} from '../../../../../libs/shared/core/src/models/api/decisionTreeNodeDataModel';
import {useDecisionTreeStore} from '../../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/hooks/useDecisionTreeStore';
import {
  BranchNodeToDelete,
  DecisionTreeNode,
  DecisionTreeNodeTypesEnum,
  TreeData,
} from '../../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/types';
import {CustomFields} from '../../../../settings/editors/commons/customFieldsModel';
import {PreExpression, serializeExpression} from '../../../../settings/editors/expressions/expressionModel';
import {ExpressionStateActions} from '../../../../settings/editors/expressions/expressionState';
import {ChatBotExpressionsEditor} from '../expressions/expressionHelpers';
import {useChatBotExpressionsEditor} from '../expressions/expressionHooks';
import {makeChatBotBuilderNode, maybeUpdatePromptReference} from '../helpers';
import {
  ChatBotBuilderNode,
  ChatBotBuilderNodeData,
  ChatBotBuilderStore,
  DeleteNodeAndDirty,
  SelectedNode,
  SetNodesAndDirty,
  UpdateNodeAndDirty,
  UpdateNodesAndDirty,
} from '../types';

/*
 * Types.
 */

type ChatBotBuilderState = TreeData<ChatBotBuilderNodeData> & {expressions: ReadonlyArray<PreExpression>};

type ChatBotBuilderStateSlices = List<ChatBotBuilderState>;

type ChatBotExpressionsEditorActions = Pick<ExpressionStateActions, 'onSaveDraftExpression'>;

/*
 * Constants.
 */

// TODO: When introducing the ability undo or redo more than one slice in a row, increase this limit.
const maxStateSlicesSize = 10;

/*
 * Hook.
 */

/**
 * Hook for retrieving the Decision Tree node and edge controlled states and helpers for their management.
 */
export function useChatBotBuilderStore(
  initialNodes: Array<DecisionTreeNode<ChatBotBuilderNodeData>>,
  initialEdges: Array<Edge>,
  initialExpressions: ReadonlyArray<PreExpression>,
  customFields: CustomFields,
  setIsDirty: (arg: boolean) => void,
) {
  const decisionTreeStore = useDecisionTreeStore<ChatBotBuilderNodeData>(initialNodes, initialEdges);

  const expressionsEditor = useChatBotExpressionsEditor(initialExpressions, customFields);

  // Store state slices in state for any change to allow popping slices off from most recent to older.
  const [stateSliceStack, setStateSliceStack] = useState<ChatBotBuilderStateSlices>(List());
  const [nodeIdsWithErrors, setNodeIdsWithErrors] = useState<ReadonlyArray<string>>([]);

  const stateSliceStackPush = useCallback(() => {
    const updatedStateSliceStack = stateSliceStack.push({
      nodes: decisionTreeStore.nodes,
      edges: decisionTreeStore.edges,
      expressions: expressionsEditor.state.expressions.map(serializeExpression),
    });
    setStateSliceStack(updatedStateSliceStack.takeLast(maxStateSlicesSize));
  }, [
    decisionTreeStore.edges,
    decisionTreeStore.nodes,
    expressionsEditor.state.expressions,
    stateSliceStack,
  ]);

  const stateSliceStackPop = () => {
    const poppedStateSlice = stateSliceStack.last(undefined);
    if (!poppedStateSlice) {
      return undefined;
    }

    const updatedStateSliceStack = stateSliceStack.pop();
    setStateSliceStack(updatedStateSliceStack);

    return poppedStateSlice;
  };

  // The setNodes provided by useDecisionTree is already synced with the controlled store, but this
  // custom setNodesAndDirty additionally sets the state of the Settings page to dirty for this resource.
  const setNodesAndDirty: SetNodesAndDirty = (nodes) => {
    setIsDirty(true);
    stateSliceStackPush();
    decisionTreeStore.setNodes(nodes);
  };

  const updateNodeAndDirty: UpdateNodeAndDirty = (id, data) => {
    setIsDirty(true);
    stateSliceStackPush();
    return decisionTreeStore.updateNode(id, data);
  };

  const updateNodesAndDirty: UpdateNodesAndDirty = (
    nodesToUpdate: Array<Pick<ChatBotBuilderNode, 'id' | 'data'>>,
  ) => {
    setIsDirty(true);
    stateSliceStackPush();

    const mutatedTree = decisionTreeStore.updateNodes(nodesToUpdate);
    return mutatedTree;
  };

  const deleteNodeAndDirty: DeleteNodeAndDirty = (node) => {
    // Always set the state dirty when deleting a node.
    setIsDirty(true);
    stateSliceStackPush();

    // If deleting a multiple choice node, we remove everything to the right and insert a placeholder.
    if (isDecisionTreeBranchNodeDataFrontChat(node.data)) {
      decisionTreeStore.deleteBranchInsertNode(
        node,
        makeChatBotBuilderNode(
          DecisionTreeNodeTypesEnum.PLACEHOLDER,
          // The placeholder is placed at the position of the deleted node.
          {x: node.position.x, y: node.position.y},
          {type: NodeDataFrontChatTypesEnum.END},
        ),
      );
    } else {
      decisionTreeStore.deleteNode(node.id);
    }
  };

  const deleteNodePreserveBranchAndDirty = (nodeId: string, preserveBranchSourceHandle: string) => {
    setIsDirty(true);
    stateSliceStackPush();

    decisionTreeStore.deleteNodePreserveBranch(nodeId, preserveBranchSourceHandle);
  };

  const deleteNodesPreserveBranchAndDirty = (nodesToDelete: ReadonlyArray<BranchNodeToDelete>) => {
    setIsDirty(true);
    stateSliceStackPush();

    decisionTreeStore.deleteNodesPreserveBranch(nodesToDelete);
  };

  const deletePromptNodeAndDirty = (nodeId: string) => {
    setIsDirty(true);
    stateSliceStackPush();
    const mutatedTree = decisionTreeStore.deleteNode(nodeId);
    const nextNodes = mutatedTree.nodes.map((node) =>
      maybeUpdatePromptReference(node, mutatedTree.nodes, mutatedTree.edges),
    );

    // Set all nodes
    setNodesAndDirty(nextNodes);
  };

  const undo = () => {
    // Pop off the previous slice.
    const poppedStateSlice = stateSliceStackPop();
    if (!poppedStateSlice) {
      return;
    }

    // Set current nodes and edges to previous state.
    decisionTreeStore.setNodes(poppedStateSlice.nodes);
    decisionTreeStore.setEdges(poppedStateSlice.edges);

    // Set expressions to the previous state.
    expressionsEditor.actions.onReplaceExpressions(poppedStateSlice.expressions);
  };

  const [selectedNode, setSelectedNode] = useState<SelectedNode | undefined>();

  const chatBotBuilderStore: ChatBotBuilderStore = {
    ...decisionTreeStore,
    setNodesAndDirty,
    updateNodeAndDirty,
    updateNodesAndDirty,
    deleteNodeAndDirty,
    deleteNodePreserveBranchAndDirty,
    deleteNodesPreserveBranchAndDirty,
    deletePromptNodeAndDirty,
    undo,
    selectedNode,
    setSelectedNode,
    nodeIdsWithErrors,
    setNodeIdsWithErrors,
    expressionsEditor: applyDirtyExpressionsEditorActions(expressionsEditor, setIsDirty, stateSliceStackPush),
  };

  return chatBotBuilderStore;
}

/*
 * Helpers.
 */

/**
 * Replaces actions on an existing ChatBotExpressionsEditor with variations that handle pushing to the state slice stack
 * and setting the state to dirty.
 */
function applyDirtyExpressionsEditorActions(
  expressionsEditor: ChatBotExpressionsEditor,
  setIsDirty: (arg: boolean) => void,
  stateSliceStackPush: () => void,
): ChatBotExpressionsEditor {
  // Set the state to dirty and push a new state slice onto the stack when saving a draft expression.
  const chatBotActions: ChatBotExpressionsEditorActions = {
    onSaveDraftExpression: (name?: string) => {
      setIsDirty(true);
      stateSliceStackPush();
      expressionsEditor.actions.onSaveDraftExpression(name);
    },
  };

  return {
    ...expressionsEditor,
    actions: {
      ...expressionsEditor.actions,
      ...chatBotActions,
    },
  };
}
